/**
 * `defer-mobile-geolocation-presets` (2026-05-20) — curated
 * geolocation pin library + waypoint helpers.
 *
 * Why
 *
 *   The mobile emulator picker already accepts a lat/lon pair
 *   ([`defer-mobile-geolocation`](#/handbook/feat-mobile-emulator-polish-cluster)
 *   2026-05-19) but offers no jumping-off point. QA engineers
 *   end up Googling "Big Ben latitude" or pasting their own
 *   home coordinates by accident.
 *
 *   This module ships ~24 pins that cover:
 *     • Major world cities for human-readable geofence tests.
 *     • Data-centre regions (AWS / GCP / Azure) so tests can
 *       align with the region they're hitting.
 *     • Edge cases (poles, equator, anti-meridian) for boundary
 *       behaviour.
 *
 *   Plus a `buildPathBetween` helper that fills a waypoint array
 *   between two pins so a "device moving from London to Paris"
 *   test is one click instead of seven.
 *
 * Why in `@rynora/contracts`
 *
 *   The picker (frontend) and the runner (backend) both need
 *   the same lookup table. Putting it in contracts keeps the
 *   front/back boundary aligned without a duplicate registry.
 */

import type { Geolocation, GeolocationWaypoint } from './mobile-emulation';

export interface GeolocationPreset {
  /** Stable id surfaced in the picker dropdown. */
  id: string;
  /** Human-readable label. */
  label: string;
  /** One-line tooltip — what this pin is useful for. */
  description: string;
  /** Geographic category (city / datacenter / edge / random). */
  category: 'city' | 'datacenter' | 'edge';
  latitude: number;
  longitude: number;
  /** Altitude metres above sea level (where known). */
  altitude?: number;
}

export const GEOLOCATION_PRESETS: GeolocationPreset[] = [
  // ----- Major cities ---------------------------------------------------
  {
    id: 'london-uk',
    label: 'London, UK',
    description: 'Trafalgar Square — UK English locale + GMT.',
    category: 'city',
    latitude: 51.5074,
    longitude: -0.1278,
    altitude: 11,
  },
  {
    id: 'new-york-us',
    label: 'New York, US',
    description: 'Times Square — US English locale + EST.',
    category: 'city',
    latitude: 40.7589,
    longitude: -73.9851,
    altitude: 10,
  },
  {
    id: 'san-francisco-us',
    label: 'San Francisco, US',
    description: 'SoMa — common tech-co HQ region.',
    category: 'city',
    latitude: 37.7749,
    longitude: -122.4194,
    altitude: 16,
  },
  {
    id: 'paris-fr',
    label: 'Paris, FR',
    description: 'Eiffel Tower — French locale + Euro.',
    category: 'city',
    latitude: 48.8566,
    longitude: 2.3522,
    altitude: 35,
  },
  {
    id: 'berlin-de',
    label: 'Berlin, DE',
    description: 'Brandenburg Gate — German locale + Euro.',
    category: 'city',
    latitude: 52.52,
    longitude: 13.405,
    altitude: 34,
  },
  {
    id: 'tokyo-jp',
    label: 'Tokyo, JP',
    description: 'Shibuya Crossing — Japanese locale + JST.',
    category: 'city',
    latitude: 35.6595,
    longitude: 139.7005,
    altitude: 40,
  },
  {
    id: 'sydney-au',
    label: 'Sydney, AU',
    description: 'Opera House — Australian English + AEST.',
    category: 'city',
    latitude: -33.8568,
    longitude: 151.2153,
    altitude: 6,
  },
  {
    id: 'mumbai-in',
    label: 'Mumbai, IN',
    description: 'Marine Drive — IST + Devanagari rendering.',
    category: 'city',
    latitude: 19.076,
    longitude: 72.8777,
    altitude: 14,
  },
  {
    id: 'singapore-sg',
    label: 'Singapore',
    description: 'Marina Bay — multi-locale stress test.',
    category: 'city',
    latitude: 1.2833,
    longitude: 103.8607,
    altitude: 5,
  },
  {
    id: 'sao-paulo-br',
    label: 'São Paulo, BR',
    description: 'Avenida Paulista — pt-BR locale.',
    category: 'city',
    latitude: -23.5613,
    longitude: -46.6562,
    altitude: 760,
  },
  {
    id: 'mexico-city-mx',
    label: 'Mexico City, MX',
    description: 'Zócalo — es-MX locale + high altitude.',
    category: 'city',
    latitude: 19.4326,
    longitude: -99.1332,
    altitude: 2240,
  },
  {
    id: 'dubai-ae',
    label: 'Dubai, AE',
    description: 'Burj Khalifa — Arabic locale + RTL.',
    category: 'city',
    latitude: 25.1972,
    longitude: 55.2744,
    altitude: 5,
  },

  // ----- Data-centre regions --------------------------------------------
  {
    id: 'aws-us-east-1',
    label: 'AWS us-east-1',
    description: 'Ashburn, Virginia — most common US region.',
    category: 'datacenter',
    latitude: 39.0438,
    longitude: -77.4874,
  },
  {
    id: 'aws-eu-west-1',
    label: 'AWS eu-west-1',
    description: 'Dublin, Ireland — most common EU region.',
    category: 'datacenter',
    latitude: 53.3331,
    longitude: -6.2489,
  },
  {
    id: 'aws-ap-southeast-1',
    label: 'AWS ap-southeast-1',
    description: 'Singapore — APAC region.',
    category: 'datacenter',
    latitude: 1.3521,
    longitude: 103.8198,
  },
  {
    id: 'gcp-us-central1',
    label: 'GCP us-central1',
    description: 'Council Bluffs, Iowa — common US region.',
    category: 'datacenter',
    latitude: 41.262,
    longitude: -95.8608,
  },
  {
    id: 'azure-westeurope',
    label: 'Azure westeurope',
    description: 'Amsterdam — multi-AZ EU region.',
    category: 'datacenter',
    latitude: 52.3676,
    longitude: 4.9041,
  },

  // ----- Edge cases -----------------------------------------------------
  {
    id: 'equator-zero',
    label: 'Equator / prime meridian',
    description: 'Lat 0 / Lon 0 — Atlantic Ocean off Africa. Boundary smoke test.',
    category: 'edge',
    latitude: 0,
    longitude: 0,
  },
  {
    id: 'south-pole',
    label: 'South Pole',
    description: 'Antarctic — extreme latitude boundary.',
    category: 'edge',
    latitude: -89.99,
    longitude: 0,
    altitude: 2835,
  },
  {
    id: 'north-pole',
    label: 'North Pole',
    description: 'Arctic — extreme latitude boundary.',
    category: 'edge',
    latitude: 89.99,
    longitude: 0,
  },
  {
    id: 'antimeridian-east',
    label: 'Anti-meridian (east)',
    description: 'Just east of the international date line.',
    category: 'edge',
    latitude: 0,
    longitude: 179.999,
  },
  {
    id: 'antimeridian-west',
    label: 'Anti-meridian (west)',
    description: 'Just west of the international date line.',
    category: 'edge',
    latitude: 0,
    longitude: -179.999,
  },
  {
    id: 'mount-everest',
    label: 'Mount Everest summit',
    description: 'Maximum-altitude smoke test (8,848 m).',
    category: 'edge',
    latitude: 27.9881,
    longitude: 86.925,
    altitude: 8848,
  },
  {
    id: 'dead-sea',
    label: 'Dead Sea',
    description: 'Negative-altitude smoke test (-430 m).',
    category: 'edge',
    latitude: 31.5,
    longitude: 35.5,
    altitude: -430,
  },
];

const PRESET_BY_ID = new Map(GEOLOCATION_PRESETS.map((p) => [p.id, p] as const));

/**
 * Lookup by id. Returns null when the id is unknown so callers
 * can decide whether to fall back to a custom pin or surface an
 * error.
 */
export function getGeolocationPreset(id: string): GeolocationPreset | null {
  return PRESET_BY_ID.get(id) ?? null;
}

/**
 * Apply a preset to an existing `Geolocation` shape — copies
 * lat/lon/altitude over, leaves accuracy / heading / speed alone
 * so user overrides survive.
 */
export function applyGeolocationPreset(preset: GeolocationPreset, current?: Partial<Geolocation>): Geolocation {
  return {
    ...(current ?? {}),
    latitude: preset.latitude,
    longitude: preset.longitude,
    altitude: preset.altitude,
  };
}

// ---------------------------------------------------------------------------
// Waypoint helpers
// ---------------------------------------------------------------------------

export interface BuildPathOptions {
  /** Number of intermediate waypoints to interpolate. Default 5. */
  steps?: number;
  /** Milliseconds to dwell at each intermediate waypoint. Default 1000. */
  dwellMs?: number;
}

/**
 * Build a linearly interpolated waypoint path from `from` to
 * `to`. The resulting array starts at `from`, includes `steps`
 * intermediate waypoints, and ends at `to`. Each intermediate +
 * the final waypoint carries the supplied `dwellMs`.
 *
 * Note: interpolation is linear on lat/lon — fine for short
 * distances (city-to-city) but degrades over very long paths
 * because the earth is curved. The picker preview surfaces a
 * "approximation" warning if the great-circle distance exceeds
 * 500 km.
 */
export function buildPathBetween(
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
  options: BuildPathOptions = {},
): GeolocationWaypoint[] {
  const steps = Math.max(0, options.steps ?? 5);
  const dwell = Math.max(0, options.dwellMs ?? 1000);

  const out: GeolocationWaypoint[] = [{ latitude: from.latitude, longitude: from.longitude, dwellMs: dwell }];
  for (let i = 1; i <= steps; i += 1) {
    const t = i / (steps + 1);
    out.push({
      latitude: from.latitude + (to.latitude - from.latitude) * t,
      longitude: from.longitude + (to.longitude - from.longitude) * t,
      dwellMs: dwell,
    });
  }
  out.push({ latitude: to.latitude, longitude: to.longitude, dwellMs: dwell });
  return out;
}

/**
 * Return the (lat, lon) point at fractional position `t` along
 * the supplied waypoint sequence. `t` is `0..1`. Used by the
 * runner to compute the device's position at any moment during
 * playback without re-walking the array each tick.
 */
export function interpolateWaypoints(
  waypoints: GeolocationWaypoint[],
  t: number,
): { latitude: number; longitude: number } | null {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return null;
  const clamped = Math.max(0, Math.min(1, t));
  if (waypoints.length === 1) {
    return { latitude: waypoints[0].latitude, longitude: waypoints[0].longitude };
  }
  // Treat all dwells as equal weight in the picker preview. The
  // runner is free to use dwellMs for time-aware playback.
  const segments = waypoints.length - 1;
  const scaled = clamped * segments;
  const idx = Math.min(segments - 1, Math.floor(scaled));
  const localT = scaled - idx;
  const a = waypoints[idx];
  const b = waypoints[idx + 1];
  return {
    latitude: a.latitude + (b.latitude - a.latitude) * localT,
    longitude: a.longitude + (b.longitude - a.longitude) * localT,
  };
}

/**
 * Great-circle distance between two pins in kilometres. Returns
 * 0 for identical pins. Used by the picker to warn when a
 * linear interpolation between very-distant pins will look
 * obviously wrong on a map.
 */
export function greatCircleKm(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const R = 6371; // Earth radius in km.
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}
